import {
  AuditingAction,
  ChangelogEntityType,
  PriorityLevel,
  TaskStatus,
  TaskVersionChangeRecordDto,
  TaskVersionChangeRecordFields,
  TaskVersionChangelogItemDto,
} from '@sib/api';
import { NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { useTranslateCategory } from '@sib/ui';
import { usePriorityOptions } from '@sib/ui/hooks';
import { DUE_DATE_FORMAT, useTaskStatusLabel } from '@sib/utils';
import {
  EntityActivityLogActionKeys,
  EntityActivityLogDescription,
  EntityActivityLogDescriptionKind,
} from '@sidecar/common/components/DetailsModal';
import { format } from 'date-fns';

const TOGGLEABLE_FIELDS = new Set<TaskVersionChangeRecordFields>([
  TaskVersionChangeRecordFields.VersionCompleted,
]);

/** Internal/audit-only fields not surfaced in the activity log UI. */
const HIDDEN_FIELDS = new Set<TaskVersionChangeRecordFields>([
  TaskVersionChangeRecordFields.VersionCompletedAt,
  TaskVersionChangeRecordFields.VersionSeenAt,
  TaskVersionChangeRecordFields.VersionSeen,
  TaskVersionChangeRecordFields.VersionRecurringTask,
  TaskVersionChangeRecordFields.VersionSubauditId,
  TaskVersionChangeRecordFields.VersionHiddenBefore,
  TaskVersionChangeRecordFields.VersionCreatedBy,
  TaskVersionChangeRecordFields.VersionUpdatedBy,
  TaskVersionChangeRecordFields.VersionDeletedAt,
]);

const isVisibleChange = (change: TaskVersionChangeRecordDto) =>
  change.prevValue !== change.newValue && !HIDDEN_FIELDS.has(change.fieldName);

const isToggleableField = (fieldName: TaskVersionChangeRecordFields) =>
  TOGGLEABLE_FIELDS.has(fieldName);

export const useTaskActivityLogLabels = () => {
  const { t } = useTranslation();
  const priorityOptions = usePriorityOptions();
  const translateCategory = useTranslateCategory();
  const statusLabel = useTaskStatusLabel();

  const actionKeys: EntityActivityLogActionKeys = {
    [EntityActivityLogDescriptionKind.FieldChanged]:
      'components.taskDetails.activityLog.actions.fieldChanged',
    [EntityActivityLogDescriptionKind.FieldToggled]: {
      on: 'components.taskDetails.activityLog.actions.fieldToggledOn',
      off: 'components.taskDetails.activityLog.actions.fieldToggledOff',
      onWithName:
        'components.taskDetails.activityLog.actions.fieldToggledOnWithName',
      offWithName:
        'components.taskDetails.activityLog.actions.fieldToggledOffWithName',
    },
    [EntityActivityLogDescriptionKind.RelatedItemAdded]:
      'components.taskDetails.activityLog.actions.relatedItemAdded',
    [EntityActivityLogDescriptionKind.RelatedItemDeleted]:
      'components.taskDetails.activityLog.actions.relatedItemDeleted',
    [EntityActivityLogDescriptionKind.EntityCreated]:
      'components.taskDetails.activityLog.actions.taskCreated',
  };

  const fieldLabels: Record<TaskVersionChangeRecordFields, string> = {
    [TaskVersionChangeRecordFields.VersionTitle]: t(
      'components.taskDetails.activityLog.fields.title'
    ),
    [TaskVersionChangeRecordFields.VersionDescription]: t(
      'components.taskDetails.activityLog.fields.description'
    ),
    [TaskVersionChangeRecordFields.VersionPriorityLevel]: t(
      'components.taskDetails.activityLog.fields.priorityLevel'
    ),
    [TaskVersionChangeRecordFields.VersionReferenceLink]: t(
      'components.taskDetails.activityLog.fields.referenceLink'
    ),
    [TaskVersionChangeRecordFields.VersionReferenceName]: t(
      'components.taskDetails.activityLog.fields.referenceName'
    ),
    [TaskVersionChangeRecordFields.VersionCategory]: t(
      'components.taskDetails.activityLog.fields.category'
    ),
    [TaskVersionChangeRecordFields.VersionStatus]: t(
      'components.tasksTable.columns.status'
    ),
    [TaskVersionChangeRecordFields.VersionAssignee]: t(
      'components.taskDetails.activityLog.fields.assignee'
    ),
    [TaskVersionChangeRecordFields.VersionOrganization]: t(
      'components.taskDetails.activityLog.fields.organization'
    ),
    [TaskVersionChangeRecordFields.VersionVendor]: t(
      'components.taskDetails.activityLog.fields.vendor'
    ),
    [TaskVersionChangeRecordFields.VersionCompleted]: t(
      'components.taskDetails.activityLog.fields.completed'
    ),
    [TaskVersionChangeRecordFields.VersionCompletedAt]: t(
      'components.taskDetails.activityLog.fields.completedAt'
    ),
    [TaskVersionChangeRecordFields.VersionDeadline]: t(
      'components.taskDetails.activityLog.fields.deadline'
    ),
    [TaskVersionChangeRecordFields.VersionSeenAt]: t(
      'components.taskDetails.activityLog.fields.seenAt'
    ),
    [TaskVersionChangeRecordFields.VersionSeen]: t(
      'components.taskDetails.activityLog.fields.seen'
    ),
    [TaskVersionChangeRecordFields.VersionFollowUpTaskParent]: t(
      'components.taskDetails.activityLog.fields.followUpTaskParent'
    ),
    [TaskVersionChangeRecordFields.VersionRecurringTask]: t(
      'components.taskDetails.activityLog.fields.recurringTask'
    ),
    [TaskVersionChangeRecordFields.VersionSubauditId]: t(
      'components.taskDetails.activityLog.fields.subauditId'
    ),
    [TaskVersionChangeRecordFields.VersionTimeLogged]: t(
      'components.taskDetails.activityLog.fields.timeLogged'
    ),
    [TaskVersionChangeRecordFields.VersionEstimatedTime]: t(
      'components.taskDetails.activityLog.fields.estimatedTime'
    ),
    [TaskVersionChangeRecordFields.VersionHiddenBefore]: t(
      'components.taskDetails.activityLog.fields.hiddenBefore'
    ),
    [TaskVersionChangeRecordFields.VersionProject]: t(
      'components.taskDetails.activityLog.fields.project'
    ),
    [TaskVersionChangeRecordFields.VersionTicket]: t(
      'components.taskDetails.activityLog.fields.ticket'
    ),
    [TaskVersionChangeRecordFields.VersionCreatedBy]: t(
      'components.taskDetails.activityLog.fields.createdBy'
    ),
    [TaskVersionChangeRecordFields.VersionUpdatedBy]: t(
      'components.taskDetails.activityLog.fields.updatedBy'
    ),
    [TaskVersionChangeRecordFields.VersionDeletedAt]: t(
      'components.taskDetails.activityLog.fields.deletedAt'
    ),
    [TaskVersionChangeRecordFields.TagsCreated]: t(
      'components.taskDetails.activityLog.fields.tagsCreated'
    ),
    [TaskVersionChangeRecordFields.TagsDeleted]: t(
      'components.taskDetails.activityLog.fields.tagsDeleted'
    ),
  };

  const formatDisplayValue = (
    fieldName: TaskVersionChangeRecordFields,
    value: string
  ) => {
    if (!value) return NO_DATA_PLACEHOLDER;

    switch (fieldName) {
      case TaskVersionChangeRecordFields.VersionPriorityLevel:
        return (
          priorityOptions.find(
            option => option.value === (value as PriorityLevel)
          )?.label ?? value
        );
      case TaskVersionChangeRecordFields.VersionCategory:
        return translateCategory(value);
      case TaskVersionChangeRecordFields.VersionStatus:
        return statusLabel[value as TaskStatus] ?? value;
      case TaskVersionChangeRecordFields.VersionDeadline: {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return format(date, DUE_DATE_FORMAT);
      }
      default:
        return value;
    }
  };

  const getItemDescriptions = (
    item: TaskVersionChangelogItemDto
  ): EntityActivityLogDescription[] => {
    if (item.type === ChangelogEntityType.TaskComment) {
      const itemType = t('components.taskDetails.activityLog.fields.comment');

      if (item.action === AuditingAction.CREATE)
        return [
          {
            kind: EntityActivityLogDescriptionKind.RelatedItemAdded,
            itemType,
            name: item.display,
          },
        ];

      if (item.action === AuditingAction.DELETE)
        return [
          {
            kind: EntityActivityLogDescriptionKind.RelatedItemDeleted,
            itemType,
            name: item.display,
          },
        ];

      return [];
    }

    if (
      item.type === ChangelogEntityType.Task &&
      item.action === AuditingAction.CREATE
    )
      return [{ kind: EntityActivityLogDescriptionKind.EntityCreated }];

    if (item.type === ChangelogEntityType.Subtask) {
      const itemType = t('components.taskDetails.activityLog.fields.subtask');

      if (item.action === AuditingAction.CREATE)
        return [
          {
            kind: EntityActivityLogDescriptionKind.RelatedItemAdded,
            itemType,
            name: item.display,
          },
        ];

      if (item.action === AuditingAction.DELETE)
        return [
          {
            kind: EntityActivityLogDescriptionKind.RelatedItemDeleted,
            itemType,
            name: item.display,
          },
        ];
    }

    const relatedItemType =
      item.type === ChangelogEntityType.Subtask
        ? t('components.taskDetails.activityLog.fields.subtask')
        : null;

    return (item.changes ?? []).filter(isVisibleChange).map(change => {
      const fieldLabel = fieldLabels[change.fieldName] ?? change.fieldName;

      if (isToggleableField(change.fieldName))
        return {
          kind: EntityActivityLogDescriptionKind.FieldToggled,
          fieldLabel,
          enabled: change.newValue === true.toString(),
          itemType: relatedItemType,
          ...(relatedItemType && { name: item.display }),
        };

      return {
        kind: EntityActivityLogDescriptionKind.FieldChanged,
        fieldLabel,
        oldValue: formatDisplayValue(change.fieldName, change.prevValue),
        newValue: formatDisplayValue(change.fieldName, change.newValue),
      };
    });
  };

  return { t, actionKeys, getItemDescriptions };
};
